import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams } from '@/services/core/listSearch'
import { fetchAllPages } from '@/services/core/paging'
import { CurrencyAmount, DeliveryCurve } from '@/services/lineItems/lineItemsApi'
import type { Targeting } from '@/services/targeting/targeting'
import { mapRateLimits, type RateLimit } from '@/services/earnings/rateLimits'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** CPM must equal the product minimum (FIXED) or may sit above it (UNCAPPED). */
export type CpmSpecification = 'FIXED' | 'UNCAPPED'
/** Whether the line item may carry objectives other than budget. */
export type BudgetSpecification = 'ALL_OBJECTIVES' | 'BUDGET_ONLY'
/** What the product sells — mirrors AxProduct.Type. */
export type AxProductType = 'STANDARD' | 'CREATIVE_TAG' | 'POLITICAL'
/** Bid strategies the product permits — mirrors AxProduct.BidStrategyOption. */
export type BidStrategyOption =
  | 'STANDARD_ONLY'
  | 'ALLOW_VCPM'
  | 'ALLOW_DYNAMIC'
  | 'ALLOW_ALL'
  | 'SPONSORSHIP'

/** Which delivery objective the product accepts. */
export type ObjectiveSpecification = 'ANY' | 'IMPRESSIONS' | 'OPTIONAL'

/**
 * How much of a line item booked against the product has to be reviewed before
 * it can deliver — mirrors AxProduct.WorkflowType.
 */
export type LineItemWorkflowType = 'DEFAULT' | 'PROPOSED' | 'CREATIVE_ONLY'

/** Shared key, so every product picker reads one cached product list. */
export const AX_PRODUCTS_QUERY_KEY = ['axproducts']

export const AX_PRODUCT_TYPES: AxProductType[] = ['STANDARD', 'CREATIVE_TAG', 'POLITICAL']
export const CPM_SPECIFICATIONS: CpmSpecification[] = ['FIXED', 'UNCAPPED']
export const BUDGET_SPECIFICATIONS: BudgetSpecification[] = ['ALL_OBJECTIVES', 'BUDGET_ONLY']
export const OBJECTIVE_SPECIFICATIONS: ObjectiveSpecification[] = [
  'ANY',
  'IMPRESSIONS',
  'OPTIONAL',
]
export const BID_STRATEGY_OPTIONS: BidStrategyOption[] = [
  'STANDARD_ONLY',
  'ALLOW_VCPM',
  'ALLOW_DYNAMIC',
  'ALLOW_ALL',
  'SPONSORSHIP',
]
export const LINE_ITEM_WORKFLOW_TYPES: LineItemWorkflowType[] = [
  'DEFAULT',
  'PROPOSED',
  'CREATIVE_ONLY',
]

/** A named object the product points at, as the API returns it. */
export interface ObjectRef {
  id: string
  name: string
}

/** A marketplace ("programmatic") product a line item can be booked against. */
export interface AxProduct {
  id: string
  name: string
  description?: string
  objectState?: string
  createTime?: string
  updateTime?: string
  minimumCpm?: CurrencyAmount
  minimumCpc?: CurrencyAmount
  minimumBudget?: CurrencyAmount
  maximumBudget?: CurrencyAmount
  cpmSpecification: CpmSpecification
  budgetSpecification: BudgetSpecification
  objectiveSpecification: ObjectiveSpecification
  type: AxProductType
  bidStrategyOption?: BidStrategyOption
  /** How much of a booked line item needs reviewing before it can deliver. */
  lineItemWorkflowType: LineItemWorkflowType
  /** Deal id stamped on line items booked against the product. */
  dealId?: string
  /** Orders the product in self-service pickers; lowest first. */
  presentationIndex: number
  /** Tier assigned to campaigns booked against the product on submission. */
  defaultTier?: ObjectRef
  /** Publisher team that may edit the product; absent means network-owned. */
  ownerTeam?: ObjectRef
  /** Advertiser teams the product is offered to. */
  teams: ObjectRef[]
  /** Layouts a creative on a booked line item may use. */
  layouts: ObjectRef[]
  labels: string[]
  /** `[width, height]` pairs a creative may have; empty means any size. */
  allowedDimensions: number[][]
  thumbnailUrl?: string
  /** Arbitrary key/value pairs the network keeps on the product. */
  data: Record<string, string>
  /** Delivery curves the product permits; empty means it does not restrict them. */
  smoothingChoices: DeliveryCurve[]
  /**
   * The targeting the product imposes. A line item booked against the product
   * inherits it, and the server re-applies it on every save.
   */
  targeting?: Targeting
  /**
   * The `TargetType` names a line item may add on top of the product's own
   * targeting. Anything else the line item targets is discarded on save unless
   * the save asks the server to ignore the product.
   */
  optionalTargetTypes: string[]
  /**
   * Caps the product imposes. When it has any, the server replaces the line
   * item's own caps with these on every save (see AxProductEnforcement), so the
   * line item cannot edit them.
   */
  rateLimits: RateLimit[]
  validationWarnings: ValidationWarning[]
}

/**
 * A marketplace product as the API takes it. Everything but the name is
 * optional — a POST merges into the stored product, so only what is sent
 * changes, and `null` is how a value is cleared.
 */
export interface AxProductInput {
  name: string
  description?: string
  objectState?: 'ACTIVE' | 'INACTIVE' | 'HIDDEN'
  type?: AxProductType
  minimumCpm?: CurrencyAmount | null
  minimumCpc?: CurrencyAmount | null
  minimumBudget?: CurrencyAmount | null
  maximumBudget?: CurrencyAmount | null
  cpmSpecification?: CpmSpecification
  budgetSpecification?: BudgetSpecification
  objectiveSpecification?: ObjectiveSpecification
  bidStrategyOption?: BidStrategyOption
  lineItemWorkflowType?: LineItemWorkflowType
  dealId?: string | null
  presentationIndex?: number
  /** Ids, not objects — the API takes references as bare id strings. */
  defaultTier?: string | null
  ownerTeam?: string | null
  teams?: string[]
  layouts?: string[]
  labels?: string[]
  allowedDimensions?: number[][]
  data?: Record<string, string>
  smoothingChoices?: DeliveryCurve[]
  targeting?: Targeting
  optionalTargetTypes?: string[]
  rateLimits?: RateLimit[]
}

function mapRef(value: unknown): ObjectRef | undefined {
  const r = value as Record<string, unknown> | undefined
  if (!r?.id) return undefined
  return { id: r.id as string, name: (r.name as string) ?? '' }
}

function mapRefs(value: unknown): ObjectRef[] {
  return ((value as Record<string, unknown>[]) ?? [])
    .map(mapRef)
    .filter((ref): ref is ObjectRef => !!ref)
}

/** Drops the half-filled rows the dimensions editor leaves behind. */
function mapDimensions(value: unknown): number[][] {
  if (!Array.isArray(value)) return []
  return value
    .filter((pair): pair is number[] => Array.isArray(pair) && pair.length === 2)
    .filter((pair) => pair.every((n) => typeof n === 'number' && n >= 0))
}

function mapAxProduct(r: Record<string, unknown>): AxProduct {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: r.description as string | undefined,
    objectState: r.objectState as string | undefined,
    createTime: r.createTime as string | undefined,
    updateTime: r.updateTime as string | undefined,
    minimumCpm: r.minimumCpm as CurrencyAmount | undefined,
    minimumCpc: r.minimumCpc as CurrencyAmount | undefined,
    minimumBudget: r.minimumBudget as CurrencyAmount | undefined,
    maximumBudget: r.maximumBudget as CurrencyAmount | undefined,
    cpmSpecification: (r.cpmSpecification as CpmSpecification) ?? 'UNCAPPED',
    budgetSpecification: (r.budgetSpecification as BudgetSpecification) ?? 'ALL_OBJECTIVES',
    objectiveSpecification: (r.objectiveSpecification as ObjectiveSpecification) ?? 'ANY',
    type: (r.type as AxProductType) ?? 'STANDARD',
    bidStrategyOption: r.bidStrategyOption as BidStrategyOption | undefined,
    lineItemWorkflowType: (r.lineItemWorkflowType as LineItemWorkflowType) ?? 'DEFAULT',
    dealId: r.dealId as string | undefined,
    presentationIndex: (r.presentationIndex as number) ?? 0,
    defaultTier: mapRef(r.defaultTier),
    ownerTeam: mapRef(r.ownerTeam),
    teams: mapRefs(r.teams),
    layouts: mapRefs(r.layouts),
    labels: (r.labels as string[]) ?? [],
    allowedDimensions: mapDimensions(r.allowedDimensions),
    thumbnailUrl: r.thumbnailUrl as string | undefined,
    data: (r.data as Record<string, string>) ?? {},
    smoothingChoices: (r.smoothingChoices as DeliveryCurve[]) ?? [],
    targeting: r.targeting as Targeting | undefined,
    optionalTargetTypes: (r.optionalTargetTypes as string[]) ?? [],
    rateLimits: mapRateLimits(r.rateLimits),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export interface AxProductListOptions {
  /** Free-text filter, matched on name and description. */
  search?: string
  /** Include products that have been deactivated — the admin list does. */
  includeInactive?: boolean
}

export const AxProductsApi = {
  /**
   * Every product matching the options, walked page by page. A product saved
   * before types existed has none and counts as standard, which the API can't
   * filter on, so lists filter on type after loading.
   */
  getAll: (options: AxProductListOptions = {}): Promise<AxProduct[]> =>
    fetchAllPages(async (page, pageSize) => {
      const params = new URLSearchParams({
        ...searchParams('axproduct', options.search),
        context: getContext(),
        includeActive: 'true',
        includeInactive: options.includeInactive ? 'true' : 'false',
        orderBy: 'name',
        pageSize: String(pageSize),
        page: String(page),
      })
      const res = await apiFetch(`${API_BASE}/axproduct?${params}`, { headers: JSON_HEADERS })
      if (!res.ok) await throwApiError(res, 'Failed to fetch marketplace products')
      const data = (await res.json()) as { totalCount?: number }
      return {
        results: toArray<Record<string, unknown>>(data).map(mapAxProduct),
        totalCount: data.totalCount,
      }
    }),

  getById: async (id: string): Promise<AxProduct> => {
    const res = await apiFetch(`${API_BASE}/axproduct/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch marketplace product')
    return mapAxProduct(await res.json())
  },

  create: async (id: string, input: AxProductInput): Promise<AxProduct> => {
    const res = await apiFetch(`${API_BASE}/axproduct/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create marketplace product')
    return mapAxProduct(await res.json())
  },

  update: async (id: string, input: AxProductInput): Promise<AxProduct> => {
    const res = await apiFetch(`${API_BASE}/axproduct/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save marketplace product')
    return mapAxProduct(await res.json())
  },
}
